import { Component, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { finalize } from 'rxjs';
import { FinancialInformationService } from 'src/app/services/financial-information/financial-information.service';
import { TuiAlertService } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { AddOverdraftRequest } from 'src/app/models/financial-information/financial-information.model';

export interface AddCostModalResult {
  confirmed: boolean;
}

@Component({
  selector: 'app-add-cost-modal',
  templateUrl: './add-cost-modal.component.html',
  styleUrls: ['./add-cost-modal.component.css'],
})
export class AddCostModalComponent {
  @Input() orderHash!: string;
  @Input() currency: string = '€'

  readonly onClose = new EventEmitter<AddCostModalResult>();

  form: FormGroup;
  saving = false;

  constructor(
    private fb: FormBuilder,
    private bsRef: BsModalRef,
    private fiSrv: FinancialInformationService,
    private alerts: TuiAlertService,
    private translate: TranslateService
  ) {
    this.form = this.fb.group({
      concept: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(/^[a-zA-ZÀ-ÿ0-9\s\-.,()']+$/),
        ],
      ],
      price: [
        '',
        [
          Validators.required,
          Validators.pattern(/^\d+(\.\d{1,2})?$/),
          Validators.min(0.01),
          Validators.max(9999999),
        ],
      ],
    });
  }

  cancel(): void {
    this.onClose.emit({ confirmed: false });
    this.bsRef.hide();
  }

  confirm(): void {
    if (this.saving) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const payload: AddOverdraftRequest = {
      orderHash: this.orderHash,
      concept: this.form.value.concept.trim(),
      price: Number(this.form.value.price),
      comment: this.form.value.comment ?? '',
    };

    this.fiSrv
      .addOverdraft(payload)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (resp) => {
          if (resp.wasSuccessful) {
            this.alerts
              .open(
                this.translate.instant(
                  'financial-information.add_cost_modal.success_added'
                ),
                { status: 'success', autoClose: 3000 }
              )
              .subscribe();
            this.onClose.emit({ confirmed: true });
            this.bsRef.hide();
          } else {
            this.alerts
              .open(
                this.translate.instant(
                  'financial-information.add_cost_modal.error_could_not_add'
                ),
                { status: 'error', autoClose: 3000 }
              )
              .subscribe();
          }
        },
        error: () => {
          this.alerts
            .open(
              this.translate.instant(
                'financial-information.add_cost_modal.error_server'
              ),
              { status: 'error', autoClose: 3000 }
            )
            .subscribe();
        },
      });
  }
}
